import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { Navbar, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Video, Calendar, Clock, ExternalLink, 
  Play, Users, Bell
} from 'lucide-react';

import { safeArray } from '../lib/utils';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const LiveClassesPage = () => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const res = await axios.get(`${API_URL}/live-classes`);
        setClasses(safeArray(res.data));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchClasses();
  }, []);

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      const loc =
        typeof navigator !== 'undefined' && navigator.language
          ? navigator.language
          : 'en-US';
      return date.toLocaleDateString(loc, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const isUpcoming = (dateStr) => {
    try {
      return new Date(dateStr) > new Date();
    } catch {
      return true;
    }
  };

  const getStatusBadge = (status, scheduledAt) => {
    if (status === 'live') {
      return (
        <Badge className="bg-red-500 animate-pulse">
          {t('live_status_live', 'LIVE NOW')}
        </Badge>
      );
    }
    if (status === 'completed') {
      return <Badge variant="secondary">{t('live_status_completed', 'Completed')}</Badge>;
    }
    if (status === 'cancelled') {
      return <Badge variant="destructive">{t('live_status_cancelled', 'Cancelled')}</Badge>;
    }
    if (isUpcoming(scheduledAt)) {
      return <Badge className="bg-green-500">{t('live_status_upcoming', 'Upcoming')}</Badge>;
    }
    return <Badge variant="secondary">{t('live_status_scheduled', 'Scheduled')}</Badge>;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="live-classes-page">
      <Navbar />
      
      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Video className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-4xl font-serif font-medium mb-2">
              {t('live_title', 'Live Classes')}
            </h1>
            <p className="text-muted-foreground text-lg">
              {t(
                'live_subtitle',
                'Join interactive live sessions with our expert instructors'
              )}
            </p>
          </div>

          {/* Live Classes List */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2, 3, 4].map(i => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-6 bg-muted rounded w-3/4 mb-4" />
                    <div className="h-4 bg-muted rounded w-1/2 mb-2" />
                    <div className="h-4 bg-muted rounded w-1/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : classes.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Calendar className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Live Classes Scheduled</h3>
                <p className="text-muted-foreground mb-4">
                  Check back later for upcoming live sessions
                </p>
                <Button onClick={() => navigate('/courses')}>Browse Courses</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {classes.map((liveClass) => (
                <Card 
                  key={liveClass.id} 
                  className={`overflow-hidden transition-all hover:shadow-lg ${
                    liveClass.status === 'live' ? 'border-red-500 ring-2 ring-red-500/20' : ''
                  }`}
                  data-testid={`live-class-${liveClass.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-2">
                        {getStatusBadge(liveClass.status, liveClass.scheduled_at)}
                        <Badge variant="outline" className="capitalize">
                          {liveClass.platform}
                        </Badge>
                      </div>
                      {liveClass.status === 'live' && (
                        <div className="flex items-center gap-1 text-red-500 text-sm">
                          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                          {t('live_indicator', 'Live')}
                        </div>
                      )}
                    </div>
                    
                    <h3 className="text-xl font-semibold mb-2">{liveClass.title}</h3>
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                      {liveClass.description}
                    </p>
                    
                    <div className="space-y-2 mb-6">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        {formatDate(liveClass.scheduled_at)}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        {liveClass.duration_minutes} {t('live_minutes', 'minutes')}
                      </div>
                    </div>
                    
                    {liveClass.status === 'live' || (liveClass.status === 'scheduled' && isUpcoming(liveClass.scheduled_at)) ? (
                      <Button 
                        className={`w-full ${liveClass.status === 'live' ? 'bg-red-500 hover:bg-red-600' : ''}`}
                        onClick={() => window.open(liveClass.meeting_url, '_blank')}
                        data-testid={`join-class-${liveClass.id}`}
                      >
                        {liveClass.status === 'live' ? (
                          <>
                            <Play className="w-4 h-4 mr-2" />
                            {t('live_join_now', 'Join Now')}
                          </>
                        ) : (
                          <>
                            <ExternalLink className="w-4 h-4 mr-2" />
                            {t('live_join_class', 'Join Class')}
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button variant="outline" className="w-full" disabled>
                        {liveClass.status === 'completed'
                          ? t('live_class_ended', 'Class Ended')
                          : t('live_status_cancelled', 'Cancelled')}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Info Section */}
          <Card className="mt-12">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5 text-accent" />
                {t('live_notify_title', 'Get Notified')}
              </h3>
              <p className="text-muted-foreground">
                {t(
                  'live_notify_body',
                  "Never miss a live class! We'll send you email notifications when new live classes are scheduled. Make sure your email notifications are enabled in your account settings."
                )}
              </p>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LiveClassesPage;
